import React, { useEffect, useState } from "react";
import axios from "axios";
import { Carousel } from "primereact/carousel";
import { FaBuilding, FaAward, FaUsers, FaGlobe, FaQuoteLeft } from "react-icons/fa";
import { Card, Container, IconTile, SectionHead } from "./ui";

const responsiveOptions = [
  { breakpoint: "1248px", numVisible: 4, numScroll: 1 },
  { breakpoint: "1024px", numVisible: 3, numScroll: 1 },
  { breakpoint: "768px", numVisible: 2, numScroll: 1 },
  { breakpoint: "480px", numVisible: 1, numScroll: 1 },
];

const stats = [
  { icon: FaBuilding, number: "1200+", label: "Campus Partners" },
  { icon: FaUsers, number: "4M+", label: "Students Trained" },
  { icon: FaAward, number: "24+", label: "Years Experience" },
  { icon: FaGlobe, number: "PAN India", label: "Presence" },
];

const FALLBACK_LOGOS = [
  "VIT University",
  "SRM Institute",
  "SASTRA University",
  "IIT Delhi",
  "IIM Bangalore",
  "Anna University",
].map((Name) => ({ attributes: { Name, imageUrl: "/api/placeholder/200/100" } }));

const Clients = () => {
  const [companyLogos, setCompanyLogos] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    axios
      .get(`https://admin.smartica.co.in/api/clients?_sort=id:DESC`)
      .then((response) => setCompanyLogos(response.data.data))
      .catch((error) => {
        console.error("Error fetching client logos:", error);
        setCompanyLogos(FALLBACK_LOGOS);
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.1 }
    );
    const element = document.querySelector('[data-section="clients"]');
    if (element) observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const logoTemplate = (logo) => {
    const name = logo?.attributes?.Name ?? "Client";
    const imgUrl = logo?.attributes?.imageUrl;
    const src = imgUrl?.startsWith("http")
      ? imgUrl
      : imgUrl
      ? `https://admin.smartica.co.in${imgUrl}`
      : undefined;

    return (
      <div className={`p-3 ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
        <Card className="flex h-28 items-center justify-center p-5 hover:border-brand/40 hover:shadow-sm">
          {src ? (
            <img
              src={src}
              alt={name}
              className="max-h-14 object-contain"
              loading="lazy"
            />
          ) : (
            <div className="text-center">
              <IconTile icon={FaBuilding} className="mx-auto mb-2" size="sm" />
              <h4 className="text-xs font-semibold text-darkgrey">{name}</h4>
            </div>
          )}
        </Card>
      </div>
    );
  };

  return (
    <section data-section="clients" className="bg-white py-20">
      <Container>
        <SectionHead
          eyebrow="Our Partners"
          title="Trusted by leading institutions"
          subtitle="Join thousands of educational institutions that trust SMART for their training needs."
        />

        {/* Figures */}
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label} className="flex items-center gap-4 p-5">
              <IconTile icon={stat.icon} />
              <div>
                <div className="text-xl font-bold text-ink sm:text-2xl">
                  {stat.number}
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-darkgrey">
                  {stat.label}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Logo carousel */}
        <div className="mt-16">
          <div className="mb-6 text-center">
            <h3 className="text-xl font-bold text-ink">Our Esteemed Partners</h3>
            <p className="mt-2 text-sm text-darkgrey">
              Leading educational institutions across India
            </p>
          </div>

          <Carousel
            value={companyLogos}
            numVisible={4}
            numScroll={2}
            itemTemplate={logoTemplate}
            responsiveOptions={responsiveOptions}
            autoplayInterval={3000}
            circular={true}
            showIndicators={true}
            className="custom-carousel"
          />
        </div>

        {/* Partner quote */}
        <div className="mt-16 rounded-3xl bg-brand-50 p-8 sm:p-12">
          <div className="mx-auto max-w-3xl text-center">
            <FaQuoteLeft className="mx-auto mb-6 h-7 w-7 text-brand" />
            <blockquote className="text-lg font-medium leading-relaxed text-ink sm:text-xl">
              SMART Training Resources has been instrumental in transforming our
              students' career prospects. Their comprehensive training programs
              and expert guidance have helped thousands of our students secure
              placements in top companies.
            </blockquote>
            <div className="mt-8 flex items-center justify-center gap-3">
              <IconTile icon={FaAward} tone="solid" />
              <div className="text-left">
                <div className="text-sm font-bold text-ink">
                  University Partners
                </div>
                <div className="text-xs text-darkgrey">Across India</div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Clients;
